import { StorageController } from "src/state/StorageBloc/StorageBloc";
import type { LearnPageViewConfig } from "components/UserJourneyOverview/UserJourneyBloc";

const STORAGE_KEY = "learn-page-view-config";

interface HydrationPayload {
  userId: string;
  config: LearnPageViewConfig;
}

// Synchronous localStorage mirror of the resolved learn page view config,
// tagged with the active user id. Seeds LearnPage before the async IndexedDB
// read resolves so the page doesn't flash a loader on every visit.
export const LearnPageViewConfigHydrationCache = {
  read(): LearnPageViewConfig | null {
    const { activeUserId } = StorageController;
    if (!activeUserId) {
      return null;
    }

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as HydrationPayload;
      if (parsed.userId !== activeUserId) {
        return null;
      }

      return parsed.config;
    } catch {
      return null;
    }
  },

  write(
    config: LearnPageViewConfig,
    expectedOwnerId: string | null = StorageController.activeUserId
  ): void {
    if (
      !expectedOwnerId ||
      StorageController.activeUserId !== expectedOwnerId
    ) {
      return;
    }

    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          userId: expectedOwnerId,
          config
        } satisfies HydrationPayload)
      );
    } catch {
      // localStorage full or unavailable — hydration is best-effort.
    }
  }
};

export const clearLearnPageViewConfigHydration = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};
